/**
 * Aadhaar via DigiLocker (Digiboost Web SDK) — Rudrik
 * ======================================================
 * Uses the official Surepass Digiboost SDK from
 *   https://cdn.jsdelivr.net/gh/surepassio/surepass-digiboost-web-sdk@latest/index.min.js
 *
 *  Step 1 — POST /api/v1/surepass/digilocker/initialize
 *           → { client_id, token, gateway, expiry_seconds }
 *  Step 2 — window.DigiboostSdk({ token, gateway, selector, onSuccess, onFailure })
 *           SDK mounts its own button; on click a popup runs the full
 *           UIDAI DigiLocker OAuth. No polling needed on our side.
 *  Step 3 — On SDK `onSuccess(data)` we call
 *           POST /api/v1/surepass/digilocker/download-aadhaar
 *           with the `client_id` from Step 1 and render the masked
 *           e-Aadhaar payload on screen.
 *
 * COMPLIANCE
 *   - Aadhaar is always MASKED (last-4) end-to-end.
 *   - Session-only: nothing about the user's Aadhaar is stored client-side
 *     beyond the current React state.
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Fingerprint, ShieldCheck, CheckCircle2, XCircle, Loader2,
  RefreshCw, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import IdentityResultCard from "../components/IdentityResultCard";
import { fromDigilockerAadhaar } from "../lib/identityNormalizers";

const API = `${window.location.origin}/api/v1/surepass/digilocker`;
const SDK_SRC = "https://cdn.jsdelivr.net/gh/surepassio/surepass-digiboost-web-sdk@latest/index.min.js";

/** Load the Digiboost SDK once (idempotent). Resolves when `window.DigiboostSdk` is ready. */
const loadDigiboostSDK = () => new Promise((resolve, reject) => {
  if (typeof window.DigiboostSdk === "function") return resolve();
  const existing = document.querySelector(`script[src="${SDK_SRC}"]`);
  if (existing) {
    existing.addEventListener("load", () => resolve());
    existing.addEventListener("error", () => reject(new Error("Failed to load Digiboost SDK")));
    return;
  }
  const s = document.createElement("script");
  s.src = SDK_SRC; s.async = true;
  s.onload = () => resolve();
  s.onerror = () => reject(new Error("Failed to load Digiboost SDK"));
  document.head.appendChild(s);
});

export default function AadhaarVerifyPage() {
  const [phase, setPhase] = useState("idle");     // idle | initializing | ready | fetching | result | error
  const [clientId, setClientId] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const sdkContainerRef = useRef(null);
  const clientIdRef = useRef(null);              // ref mirror so SDK callbacks see latest cid
  const handledRef = useRef(false);              // one-shot guard: SDK re-fires onSuccess several times

  useEffect(() => {
    // Warm the SDK cache the moment the page opens
    loadDigiboostSDK().catch(() => { /* silent — retried on click */ });
  }, []);

  const initialize = async () => {
    setPhase("initializing"); setError(null); setResult(null);
    try {
      // 1. Get token + gateway from our backend (which calls Surepass /digilocker/initialize)
      const { data: init } = await axios.post(`${API}/initialize`, {
        signup_flow: true,
        skip_main_screen: false,
      }, { withCredentials: true, timeout: 20000 });

      const cid = init?.client_id;
      const token = init?.token;
      const gateway = init?.gateway || "sandbox";
      if (!cid || !token) {
        throw new Error(init?.error || "DigiLocker initialize did not return a token");
      }
      setClientId(cid);
      clientIdRef.current = cid;
      handledRef.current = false;               // reset the one-shot guard for this session

      // 2. Load SDK
      await loadDigiboostSDK();
      if (typeof window.DigiboostSdk !== "function") {
        throw new Error("Digiboost SDK failed to load. Please try again.");
      }

      // 3. Reset the container so a fresh SDK button is mounted every time
      if (sdkContainerRef.current) sdkContainerRef.current.innerHTML = "";
      setPhase("ready");

      // Give React a tick to mount the container before the SDK looks it up
      await new Promise((r) => setTimeout(r, 50));

      // 4. Initialize the SDK — SDK will mount a button inside `#rudrik-dl-container`
      window.DigiboostSdk({
        gateway,
        token,
        selector: "#rudrik-dl-container",
        style: {
          background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
          color: "white",
          padding: "12px 24px",
          borderRadius: "6px",
          fontSize: "13px",
          fontWeight: 700,
          border: "none",
          cursor: "pointer",
          width: "auto",
        },
        onSuccess: (data) => {
          // Surepass SDK fires onSuccess multiple times for the same session
          // (postMessage on both `verified` and `closed` events). Without a
          // guard we would hit /download-aadhaar twice and Surepass would
          // reject the 2nd hit with "Aadhaar Data is Already Downloaded".
          if (handledRef.current) return;
          handledRef.current = true;

          const successCid = data?.client_id || clientIdRef.current;
          if (successCid) fetchProfile(successCid);
          else {
            setError("Verification succeeded but no client_id was returned by the SDK.");
            setPhase("error");
          }
        },
        onFailure: (err) => {
          if (handledRef.current) return;      // ignore late-firing failure after success
          handledRef.current = true;
          const msg = (err && (err.message || err.error))
                     || "DigiLocker verification was cancelled or failed. Please try again.";
          setError(msg);
          setPhase("error");
          toast.error(msg);
        },
      });
      toast.success("DigiLocker ready — click the button below to open the consent popup");
    } catch (e) {
      const msg = e?.response?.data?.detail?.message
                 || e?.response?.data?.error
                 || e?.message || "Failed to initialize DigiLocker";
      setError(msg);
      setPhase("error");
      toast.error(msg);
    }
  };

  const fetchProfile = async (cid) => {
    setPhase("fetching");
    try {
      const { data } = await axios.post(`${API}/download-aadhaar`, { client_id: cid },
        { withCredentials: true, timeout: 30000 });
      const NA = "Not available on record";
      const looksEmpty = (
        (!data?.full_name || data.full_name === NA) &&
        (!data?.dob || data.dob === NA) &&
        (!data?.masked_aadhaar || data.masked_aadhaar === NA)
      );
      if (looksEmpty) {
        const rawJson = data?._raw_surepass
          ? "\n\nRAW SUREPASS RESPONSE:\n" + JSON.stringify(data._raw_surepass, null, 2)
          : "";
        setError(
          "DigiLocker returned an empty e-Aadhaar profile. This usually means the sandbox " +
          "test account has no Aadhaar seeded, or the DigiLocker session did not include " +
          "e-KYC scopes. Please re-run the flow." + rawJson
        );
        setPhase("error");
        toast.error("DigiLocker returned no data");
        return;
      }
      setResult(data);
      setPhase("result");
      toast.success(`Aadhaar verified — ${data?.full_name || "Holder"}`);
    } catch (e) {
      const raw = e?.response?.data?.detail;
      const upstream = (raw && typeof raw === "object" ? raw.message : raw) || "";
      // If Surepass returned "Already Downloaded" but our cache didn't kick in,
      // treat it as an idempotent-noop and keep the current view.
      if (/already\s*downloaded/i.test(upstream)) {
        toast("Aadhaar already verified in this session");
        // Don't overwrite a good result — bounce back to idle for a fresh run
        if (phase !== "result") { reset(); }
        return;
      }
      const msg = upstream || e?.response?.data?.error
                 || e?.message || "Failed to fetch e-Aadhaar profile";
      setError(msg);
      setPhase("error");
      toast.error(msg);
    }
  };

  const reset = () => {
    if (sdkContainerRef.current) sdkContainerRef.current.innerHTML = "";
    clientIdRef.current = null;
    handledRef.current = false;
    setPhase("idle"); setClientId(null);
    setResult(null); setError(null);
  };

  const modeBadge = { bg: "rgba(34,197,94,0.15)", border: "#22c55e55",
                      color: "#22c55e", label: "LIVE · Digiboost SDK (UIDAI)" };

  return (
    <div data-testid="aadhaar-page" style={{ color: "#f1f5f9" }}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <Fingerprint style={{ width: 26, height: 26, color: "#2563eb" }} />
          <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}>Aadhaar via DigiLocker</h1>
          <span data-testid="mode-badge" style={{ background: modeBadge.bg, color: modeBadge.color,
                                                  border: `1px solid ${modeBadge.border}`,
                                                  fontSize: 10, padding: "3px 8px", borderRadius: 4,
                                                  fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
            {modeBadge.label}
          </span>
          <span style={{ background: "rgba(148,163,184,0.12)", color: "#cbd5e1",
                         border: "1px solid #94a3b855", fontSize: 10, padding: "3px 8px",
                         borderRadius: 4, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
            Session-only
          </span>
        </div>
      </div>
      <p style={{ color: "#94a3b8", fontSize: 13, marginBottom: 18 }}>
        UIDAI-authenticated e-KYC using the official <b>Surepass Digiboost Web SDK</b>. The SDK
        handles the full DigiLocker OAuth (login → OTP → consent) inside a secure popup. On
        success, the masked, signed e-Aadhaar profile (name, DOB, gender, address, photo) is
        displayed on this screen &mdash; no file is downloaded to your device.
      </p>

      {/* IDLE — start button */}
      {phase === "idle" && (
        <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 12, padding: 22 }}>
          <div style={{ color: "#cbd5e1", fontSize: 13, lineHeight: 1.7, marginBottom: 16 }}>
            Click <b>Start DigiLocker</b> to load the Surepass SDK. Once loaded, the SDK will
            mount its own <i>Verify with DigiLocker</i> button below &mdash; click it to open
            the consent popup.
          </div>
          <button onClick={initialize} data-testid="start-digilocker-btn"
                  style={{ background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
                           border: "none", color: "white", borderRadius: 6, padding: "11px 18px",
                           fontSize: 13, fontWeight: 700, cursor: "pointer",
                           display: "inline-flex", alignItems: "center", gap: 8 }}>
            <Sparkles size={13} /> Start DigiLocker
          </button>
        </div>
      )}

      {/* INITIALIZING */}
      {phase === "initializing" && (
        <div data-testid="dl-initializing" style={{ background: "#0d1117", border: "1px solid #1e2530",
                                                     borderRadius: 12, padding: 22,
                                                     display: "flex", alignItems: "center", gap: 12 }}>
          <Loader2 className="animate-spin" size={18} style={{ color: "#2563eb" }} />
          <span style={{ color: "#cbd5e1", fontSize: 13 }}>Requesting DigiLocker token from Surepass&hellip;</span>
        </div>
      )}

      {/* READY — SDK button mounted */}
      {(phase === "ready" || phase === "fetching") && (
        <div data-testid="dl-ready" style={{ background: "#0d1117", border: "1px solid #1e2530",
                                             borderRadius: 12, padding: 22 }}>
          <div style={{ color: "#cbd5e1", fontSize: 13, marginBottom: 14,
                          display: "flex", alignItems: "center", gap: 8 }}>
            {phase === "fetching"
              ? <><Loader2 className="animate-spin" size={14} style={{ color: "#f59e0b" }} />
                  Loading masked e-Aadhaar profile to display on screen&hellip;</>
              : <><CheckCircle2 size={14} style={{ color: "#22c55e" }} />
                  DigiLocker SDK is ready. Click the button below and complete the popup flow.</>}
          </div>
          <div id="rudrik-dl-container" ref={sdkContainerRef}
               data-testid="digiboost-sdk-slot"
               style={{ display: "inline-block", marginRight: 8 }} />
          {phase === "ready" && (
            <button onClick={reset} data-testid="cancel-dl-btn"
                    style={{ background: "transparent", border: "1px solid #1e2530", color: "#94a3b8",
                             borderRadius: 6, padding: "10px 14px", fontSize: 12, fontWeight: 700,
                             cursor: "pointer", verticalAlign: "middle" }}>
              Cancel
            </button>
          )}
          <div style={{ color: "#64748b", fontSize: 10, marginTop: 14 }}>
            Client ID <span style={{ fontFamily: "ui-monospace, monospace" }}>{clientId?.slice(0, 12)}&hellip;</span>
            {" "}&middot; Session-only &middot; No polling
          </div>
        </div>
      )}

      {/* ERROR */}
      {phase === "error" && (
        <div data-testid="dl-error" style={{ background: "#0d1117", border: "1px solid #dc262655",
                                             borderRadius: 12, padding: 22 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <XCircle size={20} style={{ color: "#dc2626" }} />
            <div style={{ color: "#dc2626", fontSize: 16, fontWeight: 800 }}>DigiLocker flow failed</div>
          </div>
          <div style={{ color: "#cbd5e1", fontSize: 12, marginBottom: 14, whiteSpace: "pre-wrap" }}>
            {(error || "").includes("RAW SUREPASS") ? (
              <>
                {error.split("RAW SUREPASS")[0]}
                <details style={{ marginTop: 10 }}>
                  <summary style={{ cursor: "pointer", color: "#f59e0b", fontWeight: 700 }}>
                    Show raw Surepass response (debug)
                  </summary>
                  <pre data-testid="raw-surepass-json"
                        style={{ marginTop: 8, padding: 10, borderRadius: 6,
                                   background: "#080c12", border: "1px solid #1e2530",
                                   color: "#94a3b8", fontSize: 10, overflowX: "auto",
                                   maxHeight: 280 }}>
                    {"RAW SUREPASS" + error.split("RAW SUREPASS")[1]}
                  </pre>
                </details>
              </>
            ) : error}
          </div>
          <button onClick={reset} data-testid="retry-dl-btn"
                  style={{ background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
                           border: "none", color: "white", borderRadius: 6, padding: "9px 16px",
                           fontSize: 12, fontWeight: 700, cursor: "pointer",
                           display: "inline-flex", alignItems: "center", gap: 6 }}>
            <RefreshCw size={11} /> Try again
          </button>
        </div>
      )}

      {/* RESULT */}
      {phase === "result" && result && (
        <>
          <div style={{ marginBottom: 16 }}>
            <IdentityResultCard person={fromDigilockerAadhaar(result)} />
          </div>
          <div data-testid="aadhaar-result-card"
               style={{ background: "#0d1117", border: "1px solid #22c55e55",
                        borderRadius: 12, padding: 22 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
              <CheckCircle2 size={22} style={{ color: "#22c55e" }} />
              <div style={{ flex: 1 }}>
                <div data-testid="okyc-verdict" style={{ color: "#22c55e", fontSize: 20, fontWeight: 800 }}>
                  KYC VERIFIED
                </div>
                <div style={{ color: "#94a3b8", fontSize: 12 }}>
                  Aadhaar e-KYC completed via Surepass Digiboost SDK (UIDAI).
                </div>
              </div>
              <button onClick={reset} data-testid="new-verify-btn"
                      style={{ background: "transparent", border: "1px solid #1e2530", color: "#94a3b8",
                               borderRadius: 6, padding: "8px 14px", fontSize: 11, fontWeight: 700,
                               cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
                <RefreshCw size={11} /> New Verification
              </button>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between",
                          color: "#64748b", fontSize: 10, marginTop: 6,
                          paddingTop: 10, borderTop: "1px solid #1e2530" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <ShieldCheck size={9} /> Source: {result?._source?.provider || "DigiLocker (UIDAI)"}
              </span>
              {result?._fetched_at && <span>Fetched: {new Date(result._fetched_at).toLocaleString("en-IN")}</span>}
              {result?.xml_url && (
                <a href={result.xml_url} target="_blank" rel="noopener noreferrer"
                    data-testid="xml-download-link"
                    style={{ color: "#2563eb", textDecoration: "none", fontWeight: 700 }}>
                  Signed XML ↗
                </a>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
